/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { isEmpty } from 'utils/isEmpty';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { avatarVariant } from '../../registry/constants';
import { defaultValue } from './Avatar';

const AvatarManager: ComponentManagerFn<'Avatar'> = (
  platform: Platform,
): ComponentManager<'Avatar'> => {
  return {
    ...baseComponentManager(platform, 'Avatar'),
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Popover', 'Menu'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
        required: (item: RequiredItem<'Avatar'>) => isEmpty(item.props.icon),
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Icon to be displayed. Only Text/Icon will be displayed',
        parentColorProp: 'color',
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: avatarVariant,
        required: true,
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
      },
      size: {
        label: 'Size',
        type: 'number',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.text.value,
    category: 'Toolbar',
    description: `**Overview**<br/>
The Avatar component is a versatile visual element used to represent users, entities, or objects. It typically renders as a circle or rounded square containing initials, an icon, or an image. It is commonly found in user profiles, lists, and toolbars to provide quick visual identification.
Use this component to display user profile pictures, assign visual badges to list items, or create icon-based buttons.

**1. Content Configuration**<br/>
The avatar can display either text or an icon.
* **Text:** A string or expression used to generate the label (e.g., user initials like "JD"). This is required if no icon is selected.
* **Expression Support:** You can bind this to dynamic data, such as $.session.displayName or $.item.initials.
* **Icon:** An alternative to text, allowing you to display a standard system icon (e.g., a "User" or "Apple" logo) inside the avatar shape.

**2. Styling & Appearance**<br/>
* **Variant:** Determines the shape of the container. Options likely include circle (default) and rounded (square with soft corners).
* **Color:** Sets the background color of the avatar using the platform's theme palette (e.g., primary, warning, success), ensuring consistency with the app's design system.
* **Size:** A numeric value defining the diameter or width of the avatar component.

**3. Interactivity & Children**<br/>
The Avatar is interactive and can act as a trigger for other UI elements.
* **TriggerOnClick:** Makes the avatar clickable (e.g., navigating to a profile page).
* **Popover:** Displays a small overlay with more info when clicked or hovered.
* **Menu:** Opens a dropdown menu, turning the avatar into a menu button (common in page headers).

**4. Best Use Cases**<br/>
* **User Profiles:** Displaying user initials (or profile photo) in the top-right corner of the app header.
* **List Views:** Adding visual anchors to a list of contacts or employees.
* **Status Indicators:** Using color-coded avatars (Green/Red) with icons to show system status.
* **Brand Logos:** Displaying company logos in a "rounded" variant within a directory.`,

    icon: { iconName: 'user-circle' },
    helpRenders: [
      {
        title: 'Default Avatar',
        description: 'Avatar rendered with Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        item: {
          itemId: 'a1',
          props: defaultValue,
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Avatar With Static Text',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['text'],
        item: {
          itemId: 'a2',
          props: {
            text: {
              type: 'text',
              value: 'Steve Jobs',
            },
          },
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Avatar With Expression',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['text'],
        item: {
          itemId: 'a3',
          props: {
            text: {
              type: 'expression',
              value: '$.session.displayName',
            },
          },
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Avatar Variant',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['variant'],
        item: {
          itemId: 'a4',
          props: {
            text: {
              type: 'expression',
              value: '$.session.displayName',
            },
            variant: 'rounded',
          },
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Avatar With Icon',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['icon'],
        item: {
          itemId: 'a5',
          props: {
            icon: { iconName: 'apple', iconType: 'brands' },
          },
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Avatar Color',
        page: SamplePage,
        exampleId: 'e6',
        visibleProps: ['color'],
        item: {
          itemId: 'a6',
          props: {
            icon: { iconName: 'apple', iconType: 'brands' },
            color: 'warning',
          },
          children: [],
          type: 'Avatar',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default AvatarManager;
